/* /assets/os-flag-select.js
 *
 * OS CUSTOM - country/currency switcher modal, replacing the header
 * popout dropdown. See snippets/os-country-modal.liquid (markup, rendered
 * once) and select-currency.liquid's country branch (the trigger
 * buttons - header desktop toolbar, header mobile drawer, footer; every
 * trigger opens/toggles this one shared modal).
 *
 * Native <dialog> + .showModal() does most of the heavy lifting for
 * free: focus trap (Tab cycling contained to the dialog), Esc-to-close
 * (fires the native `close` event, handled below like every other close
 * path), top-layer stacking (renders above everything - no z-index
 * tuning needed), and ::backdrop for the dimmed overlay. autofocus on
 * the search input (markup) is honoured natively on showModal() too.
 *
 * Three things this file still owns:
 *   1. Fetches the self-hosted flag sprite (assets/os-flags-sprite.svg,
 *      271 ISO-3166-1 alpha-2 flags from lipis/flag-icons, MIT licensed)
 *      once and inlines it into the document - required, not cosmetic:
 *      cross-origin <use href="cdn-file.svg#id"> is blocked by browsers
 *      (Shopify serves /assets/ from a CDN subdomain), so the <use>
 *      elements already in the rendered HTML only resolve once a
 *      matching <symbol id="flag-xx"> exists in the SAME document.
 *   2. Open/close/toggle wiring for the modal: multiple trigger buttons,
 *      toggle-close on the same trigger, overlay-click-to-close (the
 *      standard "click landed on the dialog's own box, not a child"
 *      pattern - <dialog> has no real backdrop DOM node to listen on),
 *      scroll lock/unlock (theme.js's existing theme:scroll:lock/unlock
 *      event pair, reused rather than reinvented), and returning focus
 *      to whichever trigger opened it (native <dialog> doesn't reliably
 *      guarantee this itself across browsers).
 *   3. Inside the modal: live search filtering (name + ISO code +
 *      currency code) with the GB-pinned divider hidden while filtering,
 *      arrow-key row navigation, and row selection - clicking a row
 *      updates the hidden country_code input and the visible
 *      current-selection state WITHOUT submitting (that only happens via
 *      the Continue button, a plain submit on the localization form) -
 *      and any unconfirmed selection is reset back to the true current
 *      country if the modal is closed without confirming.
 */
(function () {
  'use strict';

  var currentScript = document.currentScript;

  function loadSprite() {
    var url = currentScript && currentScript.getAttribute('data-sprite-url');
    if (!url) return;
    fetch(url)
      .then(function (response) {
        return response.ok ? response.text() : null;
      })
      .then(function (svgText) {
        if (!svgText) return;
        var wrapper = document.createElement('div');
        wrapper.setAttribute('hidden', '');
        wrapper.innerHTML = svgText;
        document.body.insertBefore(wrapper, document.body.firstChild);
      })
      .catch(function () {});
  }

  function initCountryModal() {
    var dialog = document.getElementById('os-country-modal');
    if (!dialog) return;

    var triggers = Array.prototype.slice.call(document.querySelectorAll('[data-os-country-modal-trigger]'));
    var searchInput = dialog.querySelector('[data-os-flag-search]');
    var optionsList = dialog.querySelector('[data-os-flag-options]');
    var hiddenInput = dialog.querySelector('[data-os-country-modal-input]');
    var closeButton = dialog.querySelector('[data-os-country-modal-close]');
    var originalCountryCode = hiddenInput ? hiddenInput.value : null;
    var lastTrigger = null;

    function resetSearch() {
      if (searchInput) searchInput.value = '';
      filterOptions('');
    }

    function filterOptions(query) {
      if (!optionsList) return;
      var normalized = query.trim().toLowerCase();
      var items = optionsList.querySelectorAll('[data-os-flag-search-text]');
      items.forEach(function (item) {
        var haystack = item.getAttribute('data-os-flag-search-text') || '';
        item.style.display = haystack.indexOf(normalized) !== -1 ? '' : 'none';
      });
      // The pinned-GB/divider pairing only makes sense against the full,
      // unfiltered A-Z list - hide it while a search is active.
      var divider = optionsList.querySelector('[data-os-flag-divider]');
      if (divider) divider.style.display = normalized ? 'none' : '';
    }

    function setCurrentRow(value) {
      var currentItem = dialog.querySelector('.popout-list__item--current');
      if (currentItem) {
        currentItem.classList.remove('popout-list__item--current');
        var currentLink = currentItem.querySelector('[data-os-country-modal-option]');
        if (currentLink) currentLink.removeAttribute('aria-current');
      }
      var row = dialog.querySelector('[data-os-country-modal-option][data-value="' + value + '"]');
      if (!row) return;
      var li = row.closest('li');
      if (li) li.classList.add('popout-list__item--current');
      row.setAttribute('aria-current', 'true');
    }

    function resetSelection() {
      if (originalCountryCode === null || !hiddenInput) return;
      hiddenInput.value = originalCountryCode;
      setCurrentRow(originalCountryCode);
    }

    function getVisibleOptions() {
      if (!optionsList) return [];
      return Array.prototype.filter.call(
        optionsList.querySelectorAll('[data-os-country-modal-option]'),
        function (el) {
          return el.offsetParent !== null;
        }
      );
    }

    function openModal(trigger) {
      if (dialog.open) return;
      lastTrigger = trigger || null;
      triggers.forEach(function (t) {
        t.setAttribute('aria-expanded', t === trigger ? 'true' : 'false');
      });
      resetSearch();
      dialog.showModal();
      dialog.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        if (dialog.open) {
          dialog.close();
          return;
        }
        openModal(trigger);
      });
    });

    if (closeButton) {
      closeButton.addEventListener('click', function () {
        dialog.close();
      });
    }

    // Standard "click outside a native <dialog>" pattern: a click that
    // lands directly on the dialog element itself (its box extends to
    // cover the ::backdrop area when shown modally) rather than bubbling
    // up from a child inside the visible content.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });

    dialog.addEventListener('close', function () {
      dialog.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      triggers.forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
      resetSelection();
      resetSearch();
      if (lastTrigger) {
        lastTrigger.focus();
        lastTrigger = null;
      }
    });

    if (searchInput) {
      searchInput.addEventListener('input', function () {
        filterOptions(searchInput.value);
      });
    }

    if (optionsList) {
      optionsList.addEventListener('click', function (event) {
        var link = event.target.closest('[data-os-country-modal-option]');
        if (!link) return;
        event.preventDefault();
        if (hiddenInput) hiddenInput.value = link.getAttribute('data-value');
        setCurrentRow(link.getAttribute('data-value'));
      });
    }

    // Arrow-key roving focus between visible rows, from either the
    // search input or another row.
    dialog.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      var target = event.target;
      var isSearchInput = target === searchInput;
      var optionEl = target.closest && target.closest('[data-os-country-modal-option]');
      if (!isSearchInput && !optionEl) return;

      var options = getVisibleOptions();
      if (!options.length) return;

      event.preventDefault();

      if (event.key === 'ArrowUp' && optionEl && options.indexOf(optionEl) === 0) {
        if (searchInput) searchInput.focus();
        return;
      }

      var currentIndex = optionEl ? options.indexOf(optionEl) : -1;
      var nextIndex;
      if (event.key === 'ArrowDown') {
        nextIndex = currentIndex + 1 >= options.length ? 0 : currentIndex + 1;
      } else {
        nextIndex = currentIndex - 1 < 0 ? options.length - 1 : currentIndex - 1;
      }
      options[nextIndex].focus();
    });
  }

  loadSprite();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCountryModal);
  } else {
    initCountryModal();
  }
})();
